export type HawaiiIsland = {
  slug: string;
  name: string;
  nickname: string;
  port: string;
  portCity: string;
  mainlandPorts: string;
  oceanTransit: string;
  sailings: string;
  intro: string;
  inbound: string;
  outbound: string;
  towns: string[];
  notes: string[];
  faqs: { q: string; a: string }[];
};

const SHARED_FAQ = (island: string, port: string): { q: string; a: string }[] => [
  {
    q: `Can you ship a car from ${island} back to the mainland?`,
    a: `Yes. Westbound and eastbound moves use the same chain in reverse: we collect the vehicle on ${island}, deliver it to ${port}, book the ocean sailing, then dispatch a mainland semi-truck carrier for door delivery once the vessel discharges on the West Coast.`,
  },
  {
    q: "Can I leave belongings in the vehicle?",
    a: "No. Ocean carriers and the marine cargo policy prohibit household goods, boxes and loose items. The car travels empty apart from the factory spare, jack and floor mats.",
  },
  {
    q: "Do I need to remove my toll transponder?",
    a: "Yes. Remove or fully disable any toll transponder before pickup. Enterprise Auto Transport is not responsible for tolls or transponder charges incurred while a vehicle is in transit.",
  },
  {
    q: "What paperwork is required?",
    a: "A clear title or a lienholder authorization letter, photo ID matching the shipper, the signed bill of lading, and a notarized power of attorney if someone else is releasing or receiving the vehicle.",
  },
];

export const HAWAII_ISLANDS: HawaiiIsland[] = [
  {
    slug: "oahu",
    name: "Oahu",
    nickname: "The Gathering Place",
    port: "Honolulu Harbor",
    portCity: "Honolulu",
    mainlandPorts: "Long Beach, Oakland, Seattle and Tacoma",
    oceanTransit: "5–9 days",
    sailings: "Multiple sailings each week — the most frequent schedule in the state",
    intro:
      "Oahu is the busiest vehicle lane in Hawaii. Honolulu Harbor receives more sailings than every other island port combined, which means shorter waits between booking and departure and the widest choice of West Coast origin ports.",
    inbound:
      "Your vehicle is picked up at your mainland address on a soft-tie equipped semi-truck, delivered to the West Coast port we booked, loaded aboard a roll-on/roll-off vessel, and released at Honolulu Harbor. From there a local Oahu carrier completes door delivery anywhere from Waikiki to the North Shore.",
    outbound:
      "Leaving the island works the same way in reverse. We collect on Oahu, tender the vehicle at Honolulu Harbor, and hand it to a mainland carrier at Long Beach, Oakland, Seattle or Tacoma for final door delivery.",
    towns: [
      "Honolulu",
      "Waikiki",
      "Pearl City",
      "Aiea",
      "Kapolei",
      "Ewa Beach",
      "Mililani",
      "Wahiawa",
      "Kailua",
      "Kaneohe",
      "Waipahu",
      "Hawaii Kai",
      "Haleiwa",
      "Waianae",
      "Schofield Barracks",
      "Joint Base Pearl Harbor-Hickam",
    ],
    notes: [
      "Best island for tight timelines — frequent sailings shorten the booking window",
      "Military PCS moves to Pearl Harbor-Hickam, Schofield and MCBH Kaneohe Bay are routine for us",
      "Port meet option available if you want to skip the local delivery fee",
    ],
    faqs: [
      {
        q: "How fast can a car reach Oahu?",
        a: "With a West Coast pickup and a sailing already on the schedule, two weeks door to door is realistic. From the East Coast or Midwest, plan on three to four weeks because the overland leg adds five to seven days.",
      },
      ...SHARED_FAQ("Oahu", "Honolulu Harbor"),
    ],
  },
  {
    slug: "maui",
    name: "Maui",
    nickname: "The Valley Isle",
    port: "Kahului Harbor",
    portCity: "Kahului",
    mainlandPorts: "Long Beach, Oakland, Seattle and Tacoma",
    oceanTransit: "7–12 days",
    sailings: "Weekly direct service, plus Honolulu relay sailings",
    intro:
      "Maui vehicles arrive at Kahului Harbor, the island's only commercial cargo port. Some sailings run direct from the West Coast; others relay through Honolulu, which adds a few days but often opens an earlier departure slot.",
    inbound:
      "After the mainland truck leg and the ocean crossing, a Maui carrier collects the vehicle at Kahului and delivers to Kihei, Wailea, Lahaina, Paia or upcountry addresses in Makawao, Kula and Haiku.",
    outbound:
      "For Maui-to-mainland moves we schedule the island pickup around the sailing date so the car is not sitting at the harbor. Once it discharges on the West Coast, a mainland carrier takes it door to door.",
    towns: [
      "Kahului",
      "Wailuku",
      "Kihei",
      "Wailea",
      "Makena",
      "Lahaina",
      "Kaanapali",
      "Napili",
      "Kapalua",
      "Paia",
      "Haiku",
      "Makawao",
      "Pukalani",
      "Kula",
      "Hana",
    ],
    notes: [
      "Upcountry and Hana deliveries need extra scheduling time due to narrow roads",
      "Relay sailings via Honolulu can beat direct service when the direct vessel is full",
      "Enclosed mainland pickup available for luxury and collector cars before the ocean leg",
    ],
    faqs: [
      {
        q: "Is there direct service to Maui or does everything go through Honolulu?",
        a: "Both exist. Direct Kahului sailings are faster; relay service through Honolulu often departs sooner. We quote whichever gets your vehicle there first and tell you which routing we booked.",
      },
      ...SHARED_FAQ("Maui", "Kahului Harbor"),
    ],
  },
  {
    slug: "hawaii-island",
    name: "Hawaii Island",
    nickname: "The Big Island",
    port: "Hilo Harbor and Kawaihae Harbor",
    portCity: "Hilo / Kawaihae",
    mainlandPorts: "Long Beach, Oakland, Seattle and Tacoma",
    oceanTransit: "8–14 days",
    sailings: "Weekly service split between the east and west side ports",
    intro:
      "Hawaii Island has two cargo harbors. Hilo serves the wet east side, and Kawaihae serves the Kona and Waimea side. Choosing the correct port matters — the drive between them is roughly two hours, and the wrong pick adds cost and days.",
    inbound:
      "We route the vehicle to whichever harbor is closest to your delivery address, then a Big Island carrier handles the final leg to Kailua-Kona, Waikoloa, Waimea, Volcano or Hilo.",
    outbound:
      "Big Island departures are the longest of the island lanes. Book three to six weeks out, especially November through January and again in the spring relocation window.",
    towns: [
      "Hilo",
      "Kailua-Kona",
      "Kawaihae",
      "Waimea",
      "Waikoloa Village",
      "Captain Cook",
      "Kealakekua",
      "Honokaa",
      "Pahoa",
      "Keaau",
      "Volcano",
      "Naalehu",
      "Ocean View",
      "Holualoa",
    ],
    notes: [
      "Tell us your delivery town first — it decides whether we book Hilo or Kawaihae",
      "Longest ocean leg of the main islands; build extra buffer into your schedule",
      "Non-running vehicles need winch or lift-gate handling arranged in advance",
    ],
    faqs: [
      {
        q: "Should I ship to Hilo or Kawaihae?",
        a: "Deliveries in Hilo, Puna, Volcano and Hamakua go through Hilo Harbor. Kona, Waikoloa, Waimea and the South Kohala resorts are served through Kawaihae. We pick the port that puts your car closest to the door.",
      },
      ...SHARED_FAQ("Hawaii Island", "Hilo or Kawaihae Harbor"),
    ],
  },
  {
    slug: "kauai",
    name: "Kauai",
    nickname: "The Garden Isle",
    port: "Nawiliwili Harbor",
    portCity: "Lihue",
    mainlandPorts: "Long Beach, Oakland, Seattle and Tacoma",
    oceanTransit: "8–13 days",
    sailings: "Weekly service, most commonly relayed through Honolulu",
    intro:
      "Kauai vehicles discharge at Nawiliwili Harbor beside Lihue. Volume is lower than Oahu or Maui, so sailings fill quickly and early booking is the single biggest factor in hitting your target date.",
    inbound:
      "A Kauai carrier meets the vessel at Nawiliwili and delivers to Poipu, Kapaa, Princeville, Hanalei or anywhere along the highway loop. Port meet is popular here because the island is small.",
    outbound:
      "Kauai-to-mainland moves work best when the island pickup is timed to the sailing. We confirm the vessel before scheduling the local truck so your car is never parked at the harbor waiting.",
    towns: [
      "Lihue",
      "Kapaa",
      "Wailua",
      "Hanamaulu",
      "Poipu",
      "Koloa",
      "Kalaheo",
      "Eleele",
      "Hanapepe",
      "Waimea",
      "Kekaha",
      "Princeville",
      "Kilauea",
      "Hanalei",
    ],
    notes: [
      "Limited weekly capacity — book 3–6 weeks ahead in peak season",
      "Port meet at Nawiliwili can save the local delivery charge",
      "North shore deliveries beyond Princeville may require a scheduled window",
    ],
    faqs: [
      {
        q: "How far ahead should I book a Kauai shipment?",
        a: "Three to six weeks. Kauai has the least weekly vehicle capacity of the four main islands, and once a sailing is full the next one may be a week or more out.",
      },
      ...SHARED_FAQ("Kauai", "Nawiliwili Harbor"),
    ],
  },
  {
    slug: "molokai",
    name: "Molokai",
    nickname: "The Friendly Isle",
    port: "Kaunakakai Harbor",
    portCity: "Kaunakakai",
    mainlandPorts: "Long Beach, Oakland, Seattle and Tacoma",
    oceanTransit: "10–18 days including the inter-island barge leg",
    sailings: "Inter-island barge service from Honolulu, limited weekly slots",
    intro:
      "Molokai is served by inter-island barge out of Honolulu rather than direct mainland sailings. Your vehicle crosses the Pacific to Oahu first, then transfers to the barge that calls at Kaunakakai Harbor.",
    inbound:
      "Because there are two ocean legs, Molokai moves need the longest lead time of any Hawaiian island. We confirm the barge slot before the vehicle leaves the mainland so it is not stuck sitting on Oahu.",
    outbound:
      "Outbound Molokai vehicles barge to Honolulu, then join a mainland-bound vessel. We coordinate both bookings together and give you one delivery estimate, not two.",
    towns: [
      "Kaunakakai",
      "Kualapuu",
      "Hoolehua",
      "Maunaloa",
      "Kalae",
      "Halawa",
      "Ualapue",
      "Kamalo",
    ],
    notes: [
      "Two ocean legs: mainland to Honolulu, then barge to Kaunakakai",
      "Barge slots are limited — reserve as early as possible",
      "Port meet at Kaunakakai is standard given the island's size",
    ],
    faqs: [
      {
        q: "Why does Molokai take longer than the other islands?",
        a: "There is no direct mainland vessel service. The car sails to Honolulu, waits for the next Kaunakakai barge, then crosses the channel. That transfer typically adds three to seven days.",
      },
      ...SHARED_FAQ("Molokai", "Kaunakakai Harbor"),
    ],
  },
  {
    slug: "lanai",
    name: "Lanai",
    nickname: "The Pineapple Isle",
    port: "Kaumalapau Harbor",
    portCity: "Lanai City",
    mainlandPorts: "Long Beach, Oakland, Seattle and Tacoma",
    oceanTransit: "10–18 days including the inter-island barge leg",
    sailings: "Inter-island barge from Honolulu on a limited weekly schedule",
    intro:
      "Lanai receives vehicles through Kaumalapau Harbor by inter-island barge from Honolulu. It is the lowest-volume vehicle lane in the state, so schedules are firm and slots must be reserved well in advance.",
    inbound:
      "After the Pacific crossing to Oahu, your vehicle transfers to the Lanai barge. A local driver completes delivery to Lanai City or the resort properties at Manele and Koele.",
    outbound:
      "Departures follow the same two-leg route in reverse. We book the barge and the mainland sailing as one coordinated move with a single quoted price.",
    towns: ["Lanai City", "Manele", "Koele", "Kaumalapau"],
    notes: [
      "Smallest vehicle lane in Hawaii — plan 4–6 weeks ahead",
      "Resort and property-management deliveries handled with advance access arrangements",
      "Enclosed handling recommended for high-value vehicles before the ocean leg",
    ],
    faqs: [
      {
        q: "Can you ship a car to a Lanai resort address?",
        a: "Yes. We arrange access with the property in advance so the local driver can complete delivery without a second trip. Give us the property contact when you book.",
      },
      ...SHARED_FAQ("Lanai", "Kaumalapau Harbor"),
    ],
  },
];

export const UNSERVED_ISLANDS = [
  {
    name: "Niihau",
    reason:
      "Privately owned and closed to the public. There is no commercial harbor or vehicle service.",
  },
  {
    name: "Kahoolawe",
    reason:
      "Uninhabited reserve managed by the state. No residents, no roads network and no vehicle transport.",
  },
];

export function getIsland(slug: string) {
  return HAWAII_ISLANDS.find((island) => island.slug === slug);
}

export type IslandCostBand = {
  /** Port-to-port ocean rate range for a standard gas or hybrid passenger vehicle. */
  ocean: string;
  /** Port-to-port ocean rate range for a battery-electric vehicle (EV-approved vessel service). */
  oceanEv: string;
  west: string;
  midwest: string;
  east: string;
  note: string;
};

/** Flat Enterprise Auto Transport broker fee, already included in every total shown. */
export const BROKER_FEE = 299;

export const HAWAII_PRICING_NOTE =
  "Every Hawaii total on this site is built the same way: the published port-to-port ocean rate for your island, plus the mainland door-to-port truck leg, plus the island delivery leg, plus our flat $299 broker fee. The $299 is already included in the totals below — it is not added later.";

export const HAWAII_EV_NOTE =
  "Battery-electric vehicles ship on an EV-approved vessel service with its own ocean tariff, state-of-charge limits and stowage rules. EV totals run roughly $500 – $700 above the comparable gas or hybrid rate, and the $299 broker fee is still included. Tell us the vehicle is electric when you request your quote so we price the correct sailing the first time.";

/** How each island is served, described by harbor and routing (no carrier brand names). */
export type IslandCarrierService = {
  standard: string;
  ev: string;
};

export const ISLAND_CARRIERS: Record<string, IslandCarrierService> = {
  oahu: {
    standard:
      "Direct sailings to Honolulu Harbor from Long Beach, Oakland, San Diego, Seattle and Tacoma",
    ev: "EV-approved sailings to Honolulu Harbor from San Diego and Oakland",
  },
  maui: {
    standard: "Service to Kahului Harbor, direct or relayed through Honolulu",
    ev: "EV-approved service to Kahului Harbor via a Honolulu relay",
  },
  "hawaii-island": {
    standard: "Service to Hilo Harbor and Kawaihae Harbor",
    ev: "EV-approved service to Hilo Harbor and Kawaihae Harbor via Honolulu",
  },
  kauai: {
    standard: "Service to Nawiliwili Harbor",
    ev: "EV-approved service to Nawiliwili Harbor via a Honolulu relay",
  },
  molokai: {
    standard: "Barge relay from Honolulu to Kaunakakai Harbor",
    ev: "EV-approved cargo relayed through Honolulu to Kaunakakai Harbor",
  },
  lanai: {
    standard: "Barge relay from Honolulu to Kaumalapau Harbor",
    ev: "EV-approved cargo relayed through Honolulu to Kaumalapau Harbor",
  },
};

export function getIslandCarriers(slug: string): IslandCarrierService | undefined {
  return ISLAND_CARRIERS[slug];
}

/**
 * Indicative 2026 door-to-door totals for a standard running sedan on an open mainland leg.
 * Each total = port-to-port ocean rate + mainland truck leg + island delivery + the flat
 * $299 broker fee. The broker fee is already baked into every number below. EV ocean bands
 * follow the EV-approved vessel tariff, which is the only service that accepts electric vehicles.
 */
export const ISLAND_COSTS: Record<string, IslandCostBand> = {
  oahu: {
    ocean: "$1,650 – $2,250",
    oceanEv: "$2,150 – $2,900",
    west: "$1,750 – $2,350",
    midwest: "$2,450 – $3,050",
    east: "$2,850 – $3,550",
    note:
      "Honolulu is the cheapest island to reach because it gets the most frequent sailings from Long Beach, Oakland, San Diego, Seattle and Tacoma. Totals include the ocean rate and the $299 broker fee.",
  },
  maui: {
    ocean: "$1,900 – $2,500",
    oceanEv: "$2,400 – $3,150",
    west: "$1,950 – $2,550",
    midwest: "$2,650 – $3,250",
    east: "$3,050 – $3,750",
    note:
      "Kahului carries a neighbor-island ocean rate above Honolulu, or a Honolulu relay leg when the direct vessel is full. Totals include the ocean rate and the $299 broker fee.",
  },
  "hawaii-island": {
    ocean: "$1,950 – $2,600",
    oceanEv: "$2,450 – $3,250",
    west: "$2,000 – $2,650",
    midwest: "$2,700 – $3,300",
    east: "$3,100 – $3,850",
    note:
      "Both Hilo and Kawaihae are served; the harbor closest to your delivery address usually prices best. Totals include the ocean rate and the $299 broker fee.",
  },
  kauai: {
    ocean: "$2,000 – $2,650",
    oceanEv: "$2,500 – $3,300",
    west: "$2,050 – $2,700",
    midwest: "$2,750 – $3,350",
    east: "$3,150 – $3,900",
    note:
      "Nawiliwili gets fewer sailings, so booking three to five weeks out protects both the price and the date. Totals include the ocean rate and the $299 broker fee.",
  },
  molokai: {
    ocean: "$2,200 – $2,900",
    oceanEv: "$2,700 – $3,550",
    west: "$2,250 – $2,950",
    midwest: "$2,950 – $3,600",
    east: "$3,350 – $4,150",
    note:
      "Molokai moves relay through Honolulu on a barge schedule, which adds transit days and ocean cost. Totals include the ocean rate and the $299 broker fee.",
  },
  lanai: {
    ocean: "$2,250 – $2,950",
    oceanEv: "$2,750 – $3,600",
    west: "$2,300 – $3,000",
    midwest: "$3,000 – $3,650",
    east: "$3,400 – $4,200",
    note:
      "Lanai is barge-relayed from Honolulu on a limited schedule; flexible dates make a real difference here. Totals include the ocean rate and the $299 broker fee.",
  },
};

export function getIslandCost(slug: string): IslandCostBand | undefined {
  return ISLAND_COSTS[slug];
}

